/*
 * R5 accessible scroll-snap carousel controller — replaces Swiper.js.
 * One audited controller drives every converted carousel so the a11y behavior
 * (real <button> controls, focus, ARIA, reduced-motion, autoplay pause) is
 * consistent and WCAG 2.1 AA-friendly. Vanilla JS, no dependencies, ~2KB min+gz.
 *
 * Markup contract (see carousel.css):
 *   [data-carousel]                 root / accessible region
 *     [data-carousel-viewport]      the scroll-snap scroller (required)
 *       [data-carousel-track]       flex track (optional; defaults to viewport's first child)
 *         > slide elements          each direct child = one slide
 *     [data-carousel-prev|next]     optional control <button>s (may live outside the viewport)
 *     [data-carousel-dots]          optional host; dot <button>s are generated here
 *     [data-carousel-autoplay-toggle] optional pause/play <button> (required if autoplay)
 *   Root attrs: data-carousel-loop, data-carousel-align="center|start",
 *               data-carousel-autoplay="<ms>", data-carousel-drag, data-carousel-label="<label>",
 *               data-carousel-infinite (PARKED / opt-in clone-based infinite loop — OFF by default;
 *                                       add the attribute to a carousel root to turn it on),
 *               data-carousel-hide-inactive (hide off-screen slides from AT + tab order; for 1-up sliders)
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false };

  function prefersReduced() {
    return !!reducedMotion.matches;
  }

  function elementChildren(el) {
    return el
      ? Array.prototype.filter.call(el.children, function (n) { return n.nodeType === 1; })
      : [];
  }

  function Carousel(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-carousel-viewport]') || root;
    this.track = root.querySelector('[data-carousel-track]') || this.viewport.firstElementChild || this.viewport;
    this.slides = elementChildren(this.track);
    if (!this.slides.length) return;

    this.loop = root.hasAttribute('data-carousel-loop');
    this.align = root.getAttribute('data-carousel-align') || 'center';
    this.autoplayDelay = parseInt(root.getAttribute('data-carousel-autoplay'), 10) || 0;
    this.prevBtn = root.querySelector('[data-carousel-prev]');
    this.nextBtn = root.querySelector('[data-carousel-next]');
    this.dotsHost = root.querySelector('[data-carousel-dots]');
    this.index = 0;
    this.paused = false;
    this.hideInactive = root.hasAttribute('data-carousel-hide-inactive');

    // A single-slide carousel needs no controls.
    if (this.slides.length < 2) {
      [this.prevBtn, this.nextBtn, this.dotsHost].forEach(function (el) { if (el) el.style.display = 'none'; });
    }

    this.applyAria();
    this.bindControls();
    if (this.dotsHost) this.buildDots();
    this.bindScrollSync();
    this.update();
    if (this.autoplayDelay > 0) this.setupAutoplay();
    if (this.root.hasAttribute('data-carousel-drag')) this.setupDrag();
    if (this.root.hasAttribute('data-carousel-infinite')) this.setupInfiniteLoop();
  }

  Carousel.prototype.applyAria = function () {
    var root = this.root;
    if (!root.getAttribute('role')) root.setAttribute('role', 'region');
    root.setAttribute('aria-roledescription', 'carousel');
    var label = root.getAttribute('data-carousel-label');
    if (label && !root.getAttribute('aria-label')) root.setAttribute('aria-label', label);

    var total = this.slides.length;
    this.slides.forEach(function (slide, i) {
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      if (!slide.getAttribute('aria-label')) {
        slide.setAttribute('aria-label', (i + 1) + ' of ' + total);
      }
    });
  };

  Carousel.prototype.bindControls = function () {
    var self = this;
    if (this.prevBtn) {
      if (!this.prevBtn.getAttribute('type')) this.prevBtn.setAttribute('type', 'button');
      this.prevBtn.addEventListener('click', function () { self.go(self.index - 1, true); });
    }
    if (this.nextBtn) {
      if (!this.nextBtn.getAttribute('type')) this.nextBtn.setAttribute('type', 'button');
      this.nextBtn.addEventListener('click', function () { self.go(self.index + 1, true); });
    }
  };

  Carousel.prototype.buildDots = function () {
    var self = this;
    this.dots = [];
    this.dotsHost.setAttribute('role', 'group');
    if (!this.dotsHost.getAttribute('aria-label')) {
      this.dotsHost.setAttribute('aria-label', 'Choose slide to display');
    }
    this.slides.forEach(function (slide, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      b.addEventListener('click', function () { self.go(i, true); });
      self.dotsHost.appendChild(b);
      self.dots.push(b);
    });
  };

  Carousel.prototype.bindScrollSync = function () {
    var self = this;
    var ticking = false;
    this.viewport.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var ni = self.nearestIndex();
        if (ni !== self.index) { self.index = ni; self.update(); }
        ticking = false;
      });
    }, { passive: true });
  };

  Carousel.prototype.nearestIndex = function () {
    var center = this.viewport.scrollLeft + this.viewport.clientWidth / 2;
    var best = 0, bestDist = Infinity;
    for (var i = 0; i < this.slides.length; i++) {
      var s = this.slides[i];
      var d = Math.abs((s.offsetLeft + s.offsetWidth / 2) - center);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    return best;
  };

  Carousel.prototype.go = function (i, animate) {
    var n = this.slides.length;
    var wrapped = false;
    if (this.loop) {
      if (i < 0) { i = n - 1; wrapped = true; }
      else if (i > n - 1) { i = 0; wrapped = true; }
    } else {
      i = Math.max(0, Math.min(i, n - 1));
    }
    this.index = i;
    var smooth = animate && !wrapped && !prefersReduced();
    // Scroll ONLY this carousel's viewport — never the page. scrollIntoView() also scrolls every
    // ancestor scroller (incl. the window), so autoplay was yanking the whole page down to the
    // carousel on every tick. Compute the horizontal delta and scroll the viewport directly.
    var vp = this.viewport;
    var slide = this.slides[i];
    if (slide.offsetWidth > 0) { // skip hidden slides (the 1-up hero advances via display swap, not scroll)
      var delta = slide.getBoundingClientRect().left - vp.getBoundingClientRect().left;
      if (this.align === 'center') delta -= (vp.clientWidth - slide.offsetWidth) / 2;
      else if (this.align === 'end') delta -= (vp.clientWidth - slide.offsetWidth);
      // Direct scrollLeft assignment lands reliably on the snap point and scrolls only this viewport
      // (never the page). Programmatic scrollTo({behavior:'smooth'}) proved unreliable under
      // scroll-snap:mandatory here, so we set scrollLeft directly. CSS can add a smooth feel later.
      vp.scrollLeft = vp.scrollLeft + delta;
    }
    this.update();
  };

  Carousel.prototype.update = function () {
    var last = this.slides.length - 1;
    if (!this.loop) {
      if (this.prevBtn) this.setDisabled(this.prevBtn, this.index <= 0);
      if (this.nextBtn) this.setDisabled(this.nextBtn, this.index >= last);
    }
    if (this.dots) {
      for (var i = 0; i < this.dots.length; i++) {
        var active = i === this.index;
        this.dots[i].classList.toggle('is-active', active);
        this.dots[i].setAttribute('aria-current', active ? 'true' : 'false');
      }
    }
    this.applyInactiveHiding();
  };

  Carousel.prototype.setDisabled = function (btn, disabled) {
    btn.disabled = disabled;
    btn.setAttribute('aria-disabled', disabled ? 'true' : 'false');
  };

  // Hide inactive slides from screen readers + pull their focusables out of the tab order
  // (opt-in: data-carousel-hide-inactive). Used on the 1-up hero so AT only sees the visible slide.
  Carousel.prototype.applyInactiveHiding = function () {
    if (!this.hideInactive) return;
    var active = this.index;
    this.slides.forEach(function (slide, i) {
      var isActive = i === active;
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      Array.prototype.forEach.call(
        slide.querySelectorAll('a,button,input,textarea,select,[tabindex]'),
        function (el) {
          if (isActive) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', '-1');
        }
      );
    });
  };

  Carousel.prototype.setupAutoplay = function () {
    // No auto-rotation when the user prefers reduced motion (WCAG 2.3.3 / 2.2.2).
    if (prefersReduced()) return;
    var self = this;
    var toggle = this.root.querySelector('[data-carousel-autoplay-toggle]');

    function tick() { if (!self.paused) self.go(self.index + 1, true); }
    this.timer = window.setInterval(tick, this.autoplayDelay);

    function pause() { self.paused = true; }
    function resume() { if (!self.userStopped) self.paused = false; }
    this.root.addEventListener('mouseenter', pause);
    this.root.addEventListener('mouseleave', resume);
    this.root.addEventListener('focusin', pause);
    this.root.addEventListener('focusout', resume);

    // Explicit pause/play control satisfies WCAG 2.2.2 (pause moving content).
    if (toggle) {
      if (!toggle.getAttribute('type')) toggle.setAttribute('type', 'button');
      toggle.setAttribute('aria-pressed', 'false');
      toggle.addEventListener('click', function () {
        self.userStopped = !self.userStopped;
        self.paused = self.userStopped;
        toggle.setAttribute('aria-pressed', self.userStopped ? 'true' : 'false');
      });
    }
  };

  // Mouse/trackpad drag-to-scroll (parity with Swiper's grabCursor). Touch uses native scroll.
  Carousel.prototype.setupDrag = function () {
    var vp = this.viewport;
    var down = false, startX = 0, startScroll = 0, moved = false;
    vp.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch' || e.button !== 0) return;
      down = true; moved = false; startX = e.clientX; startScroll = vp.scrollLeft;
      try { vp.setPointerCapture(e.pointerId); } catch (err) {}
      vp.classList.add('is-dragging');
    });
    vp.addEventListener('pointermove', function (e) {
      if (!down) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 3) moved = true;
      vp.scrollLeft = startScroll - dx;
    });
    function end() {
      if (!down) return;
      down = false;
      vp.classList.remove('is-dragging');
    }
    vp.addEventListener('pointerup', end);
    vp.addEventListener('pointercancel', end);
    // Swallow the click that follows a real drag so links/cards don't fire.
    vp.addEventListener('click', function (e) {
      if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; }
    }, true);
  };

  /*
   * PARKED / opt-in: seamless infinite looping via slide-cloning (data-carousel-infinite).
   * OFF by default — no carousel sets the attribute, so this never runs and the carousels stay
   * bounded. To turn it on later, add data-carousel-infinite to a carousel root.
   * Built for free-scroll / swipe carousels (the card rows + 1-up sliders). Notes for enabling:
   *   - Only activates while the track actually overflows, so desktop-static rows stay static
   *     (clones are added when scrollable and removed on resize back to a non-scrolling width).
   *   - Clones are aria-hidden and pulled out of the tab order (no duplicate content for AT).
   *   - Best on dotless carousels — pagination dots are not recomputed against clones.
   *   - iOS momentum scrolling may need tuning at the reposition seam when this is switched on.
   */
  Carousel.prototype.setupInfiniteLoop = function () {
    var self = this;
    var state = { active: false, setWidth: 0, clones: [] };
    this._loopState = state;

    function scrollable() {
      return self.viewport.scrollWidth > self.viewport.clientWidth + 4;
    }

    function instantScroll(left) {
      var prev = self.viewport.style.scrollBehavior;
      self.viewport.style.scrollBehavior = 'auto';
      self.viewport.scrollLeft = left;
      self.viewport.style.scrollBehavior = prev;
    }

    function markClone(node) {
      node.setAttribute('aria-hidden', 'true');
      node.setAttribute('data-carousel-clone', '');
      if (node.matches && node.matches('a,button,input,select,textarea,[tabindex]')) {
        node.setAttribute('tabindex', '-1');
      }
      Array.prototype.forEach.call(
        node.querySelectorAll('a,button,input,select,textarea,[tabindex]'),
        function (f) { f.setAttribute('tabindex', '-1'); }
      );
    }

    function build() {
      if (state.active || !scrollable()) return;
      state.setWidth = self.track.scrollWidth; // width of the real set, before cloning
      var originals = self.slides.slice();
      for (var i = originals.length - 1; i >= 0; i--) {
        var pre = originals[i].cloneNode(true);
        markClone(pre);
        self.track.insertBefore(pre, self.track.firstChild);
        state.clones.push(pre);
      }
      originals.forEach(function (o) {
        var post = o.cloneNode(true);
        markClone(post);
        self.track.appendChild(post);
        state.clones.push(post);
      });
      state.active = true;
      instantScroll(state.setWidth); // start on the real set
    }

    function teardown() {
      if (!state.active) return;
      state.clones.forEach(function (c) { if (c.parentNode) c.parentNode.removeChild(c); });
      state.clones = [];
      state.active = false;
      state.setWidth = 0;
    }

    var ticking = false;
    self.viewport.addEventListener('scroll', function () {
      if (!state.active || ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var sw = state.setWidth;
        var x = self.viewport.scrollLeft;
        // When the user scrolls into a clone zone, jump one set-width to the mirrored real
        // position. Content is identical there, so the jump is invisible.
        if (x < sw * 0.5) instantScroll(x + sw);
        else if (x > sw * 1.5) instantScroll(x - sw);
        ticking = false;
      });
    }, { passive: true });

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        if (scrollable()) build();
        else teardown();
      }, 200);
    });

    build();
  };

  function initAll(ctx) {
    var scope = ctx && ctx.querySelectorAll ? ctx : document;
    var roots = scope.querySelectorAll('[data-carousel]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__carouselInit) return;
      root.__carouselInit = true;
      try { new Carousel(root); } catch (e) { /* fail open: native scroll-snap still works */ }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  // Re-init when sections are re-rendered in the Shopify theme editor.
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });

  window.SaaltCarousel = { initAll: initAll, Carousel: Carousel };
})();
